import React, { useState, useMemo, useEffect } from 'react';
import { createColumnHelper, useReactTable, getCoreRowModel, getSortedRowModel, getPaginationRowModel, flexRender } from '@tanstack/react-table';
import axios from 'axios';
import { getAPIUrl } from '../api/apiUtils';
import debounce from 'lodash/debounce';
import ExportModal from '../components/ExportModal';
import DataWindowSlider from '../components/DataWindowSlider';
import { useDataWindow } from '../hooks/useDataWindow';
import { useChunkedDataLoader } from '../hooks/useChunkedDataLoader';
import { fillDataGaps, getActualDataLength } from '../utils/dataUtils';
import './TextMarkersAnalysis.scss';
import './Sidebar.scss';
import MarkersFilter from './MarkersFilter';
import { getMarkerLabel, markerCategories, isNumericMarker, isMarkerHidden, markersDictionary, isMarkerSystem } from './markersDictionary';
import { getGroupTitleById } from '../utils/groupTitles';
import { useRegionsData } from '../hooks/useRegions';
import ChartsGroup from '../components/ChartsGroup';
import Pagination from '../components/Pagination';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const API_BASE_URL = getAPIUrl();
const columnHelper = createColumnHelper();
const MARKERS_KEY = 'selectedMarkers_markers';

const TextMarkersAnalysis = () => {
  const { regions, isLoading: isGroupsLoading } = useRegionsData();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [error, setError] = useState(null);
  const [showTable, setShowTable] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedMarkers, setSelectedMarkers] = useState(() => {
    try {
      const saved = localStorage.getItem(MARKERS_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [viewMode, setViewMode] = useState('table');
  const [aggregation, setAggregation] = useState('day');
  const [showExportModal, setShowExportModal] = useState(false);

  function getGlobalFilters() {
    try {
      const saved = localStorage.getItem('globalFilters');
      if (!saved) return { selectedGroups: [], startDate: null, endDate: null };
      const parsed = JSON.parse(saved);
      return {
        selectedGroups: Array.isArray(parsed.selectedGroups) ? parsed.selectedGroups : [],
        startDate: parsed.startDate ? parsed.startDate : null,
        endDate: parsed.endDate ? parsed.endDate : null,
      };
    } catch {
      return { selectedGroups: [], startDate: null, endDate: null };
    }
  }
    
  const [globalFilters, setGlobalFilters] = useState(getGlobalFilters());  
  
  // Инициализируем хук для ленивой загрузки данных
  const chartMarkers = useMemo(() => 
    selectedMarkers.filter(m => isNumericMarker(m)), 
    [selectedMarkers]
  );
  
  const chunkedDataLoader = useChunkedDataLoader({
    apiUrl: `${API_BASE_URL}/detection/text_markers_chart_data`,
    startDate: globalFilters.startDate,
    endDate: globalFilters.endDate,
    groupIds: globalFilters.selectedGroups,
    markers: chartMarkers,
    aggregation: aggregation,
    chunkSize: 30,
  });

  // Используем данные из хука
  const chartData = chunkedDataLoader.data;
  const chartLoading = chunkedDataLoader.loading;
  const chartError = chunkedDataLoader.error;
  
  // Обработка данных с заполнением пропусков
  const processedChartData = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return [];
    }

    const { startDate, endDate } = globalFilters;
    if (!startDate || !endDate) {
      return chartData;
    }

    // Преобразуем строковую агрегацию в числовую для fillDataGaps
    const numericAggregation = aggregation === 'day' ? '1' : aggregation === 'week' ? '7' : '30';
    
    const result = fillDataGaps(
      chartData, 
      startDate, 
      endDate, 
      numericAggregation
    );
    
    // Отладочная информация для проверки пропусков
    if (result.length > 0 && result[0].data) {
      const gapsCount = result[0].data.filter(d => d.isGap).length;
      const totalCount = result[0].data.length;
      console.log(`Пропуски в данных: ${gapsCount} из ${totalCount} точек`);
    }
    
    return result;
  }, [chartData, globalFilters, aggregation]);

  const [dataWindow, updateDataWindow] = useDataWindow(100, getActualDataLength(processedChartData));
  
  // Обновляем dataWindow при изменении processedChartData
  useEffect(() => {
    const actualLength = getActualDataLength(processedChartData);
    if (actualLength > 0) {
      // Если текущее окно выходит за границы данных, корректируем его
      if (dataWindow.end > actualLength) {
        const newStart = Math.max(0, actualLength - Math.min(100, actualLength));
        const newEnd = actualLength;
        updateDataWindow({ start: newStart, end: newEnd });
      }
      // Если данных меньше чем размер окна, показываем все данные
      else if (actualLength <= 100) {
        updateDataWindow({ start: 0, end: actualLength });
      }
    }
  }, [processedChartData]);

  useEffect(() => {
    let last = JSON.stringify(getGlobalFilters());
    const interval = setInterval(() => {
      const current = JSON.stringify(getGlobalFilters());
      if (current !== last) {
        last = current;
        setGlobalFilters(getGlobalFilters());
      }
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const categories = useMemo(() => {
    const categoriesObj = {};
    markerCategories.forEach(cat => {
      categoriesObj[cat.category] = cat.markers;
    });
    return categoriesObj;
  }, []);

  const allColumnsFlat = useMemo(
    () => Object.values(categories).flat().map(key =>
      columnHelper.accessor(key, {
        header: getMarkerLabel(key),
        cell: info => {
          if (key === 'time_stamp') {
            return new Date(info.getValue()).toLocaleString('ru-RU');
          }
          if (key === 'date') {
            return new Date(info.getValue()*1000).toLocaleString('ru-RU');
          }
          if (key === 'group_id') {
            return getGroupTitleById(info.getValue());
          }
          if (key === 'post_id') {
            return info.getValue();
          }
          const value = info.getValue();
          return isNumericMarker(key) && value != null && !isNaN(value) ? Number(value).toFixed(3) : value;
        }
      })
    ),
    [categories]
  );

  const columnCategories = useMemo(() => {
    return Object.entries(categories).map(([categoryName, columnKeys]) => ({
      category: categoryName,
      columns: allColumnsFlat.filter(col => columnKeys.includes(col.accessorKey)),
    }));
  }, [categories, allColumnsFlat]);

  const columns = useMemo(
    () => {
      console.log('columns', selectedMarkers);
      const ALWAYS_HIDDEN = new Set(['time_stamp', 'session_id']);
      const visibleMarkers = [
        ...selectedMarkers.filter((key) => !ALWAYS_HIDDEN.has(key)),
        ...Object.keys(markersDictionary).filter(
          key => isMarkerHidden(key) && !isMarkerSystem(key) && !ALWAYS_HIDDEN.has(key) && !selectedMarkers.includes(key)
        )
      ];
      return visibleMarkers.length > 0 ? allColumnsFlat.filter(col => visibleMarkers.includes(col.accessorKey)) : [];
    },
    [selectedMarkers, allColumnsFlat]
  );

  // Загрузка групп теперь происходит автоматически через useRegionsData
  

  // Эффект для начальной загрузки данных
  useEffect(() => {
    if (viewMode === 'chart' && chartMarkers.length > 0 && globalFilters.selectedGroups.length > 0) {
      console.log('Начальная загрузка данных графика');
      chunkedDataLoader.reset();
      chunkedDataLoader.loadInitialData();
    }
  }, [viewMode, globalFilters.selectedGroups, globalFilters.startDate, globalFilters.endDate, chartMarkers, aggregation]);
  
  // Функция для загрузки данных при прокрутке слайдера
  const handleDataWindowChange = (windowStart, windowEnd) => {
    if (viewMode === 'chart' && chartMarkers.length > 0) {
      console.log('Загрузка данных для окна:', windowStart, windowEnd);
      chunkedDataLoader.loadDataForWindow(windowStart, windowEnd);
    }
  };

  useEffect(() => {
    localStorage.setItem(MARKERS_KEY, JSON.stringify(selectedMarkers));
  }, [selectedMarkers]);

  const fetchData = async ({ pageIndex, pageSize, sorting }) => {
    if (selectedMarkers.length === 0) {
      setData([]);
      setPageCount(1);
      setTotalRows(0);
      return;
    }

    setLoading(true);
    try {
      const sort = sorting[0] || { id: 'date', desc: false };
      const page = Number(pageIndex) + 1;
      const { selectedGroups, startDate, endDate } = globalFilters;
      const columns = [
        ...selectedMarkers,
        ...Object.keys(markersDictionary).filter(
          key => isMarkerHidden(key) && !selectedMarkers.includes(key)
        )
      ];
      const params = {
        page,
        limit: pageSize,
        sort_by: sort.id,
        sort_order: sort.desc ? 'desc' : 'asc',
        columns: columns.length > 0 ? columns.join(',') : undefined,
        group_ids: selectedGroups.length > 0 ? selectedGroups.join(',') : undefined,
      };
      if (startDate) params.start_date = typeof startDate === 'string' ? startDate : '';
      if (endDate) params.end_date = typeof endDate === 'string' ? endDate : '';
      const response = await axios.get(`${API_BASE_URL}/detection/text_markers_analysis`, { params });

      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Неверный формат ответа сервера');
      }

      const total = typeof response.data.total === 'number' ? response.data.total : 0;
      const data = Array.isArray(response.data.data) ? response.data.data : [];
      const size = Number(pageSize) || 10;
      const pageCount = size > 0 ? Math.ceil(total / size) : 1;

      setData(data);
      setTotalRows(total);
      setPageCount(pageCount);
      setError(null);
    } catch (error) {
      console.error('Ошибка при загрузке данных:', error, error.message, error.stack);
      setError(`Не удалось загрузить данные: ${error.message}`);
      setPageCount(1);
    } finally {
      setLoading(false);
    }
  };


  

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount,
    onPaginationChange: (updater) => {
      console.log('Pagination change triggered');
      const newState = typeof updater === 'function' ? updater(table.getState().pagination) : updater;
      table.setState((old) => ({
        ...old,
        pagination: newState,
      }));
      console.log('New pagination state:', newState, 'showTable:', showTable);
      if (showTable) {
        fetchData({
          pageIndex: newState.pageIndex,
          pageSize: newState.pageSize,
          sorting: table.getState().sorting,
        });
      }
    },
    onSortingChange: (updater) => {
      console.log('Sorting change triggered');
      const newState = typeof updater === 'function' ? updater(table.getState().sorting) : updater;
      table.setState((old) => ({
        ...old,
        sorting: newState,
      }));
      if (showTable) {
        fetchData({
          pageIndex: table.getState().pagination.pageIndex,
          pageSize: table.getState().pagination.pageSize,
          sorting: newState,
        });
      }
    },
    initialState: {
      pagination: { pageIndex: 0, pageSize: 10 },
      sorting: [{ id: 'date', desc: false }],
    },
  });

  // Загрузка данных таблицы при смене режима
  useEffect(() => {
    if (!showTable) return;
    if (viewMode === 'table' && data.length === 0 && selectedMarkers.length > 0) {
      fetchData({
        pageIndex: table.getState().pagination.pageIndex,
        pageSize: table.getState().pagination.pageSize,
        sorting: table.getState().sorting,
      });
    }
  }, [viewMode]);

  // Автоматическая загрузка данных таблицы при монтировании компонента
  useEffect(() => {
    if (selectedMarkers.length > 0 && viewMode === 'table') {
      fetchData({
        pageIndex: table.getState().pagination.pageIndex,
        pageSize: table.getState().pagination.pageSize,
        sorting: table.getState().sorting,
      });
    }
  }, []);

  const maxDataLength = useMemo(() => {
    const start = globalFilters.startDate ? new Date(globalFilters.startDate) : new Date();
    const end = globalFilters.endDate ? new Date(globalFilters.endDate) : new Date();
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    console.log(diffDays)
    return diffDays;
  }, [globalFilters.startDate, globalFilters.endDate]);

  const parseISOWeek = (isoWeekStr) => {
    const [year, week] = isoWeekStr.split('-W').map(Number);
    // Создаем дату для первого дня года
    const date = new Date(year, 0, 1);
    // Находим первый понедельник года
    const firstDayOfYear = date.getDay();
    const daysToFirstMonday = firstDayOfYear === 0 ? 1 : firstDayOfYear === 1 ? 0 : 8 - firstDayOfYear;
    // Вычисляем начало указанной недели
    const daysToAdd = (week - 1) * 7 + daysToFirstMonday;
    date.setDate(date.getDate() + daysToAdd);
    return { year, week };
  };  

  const formatWeekLabel = (isoWeekStr) => {
    const { year, week } = parseISOWeek(isoWeekStr);
    return `${week}-я неделя ${year} года`;
  };


  const markerColors = useMemo(() => {
    const colors = [
      '#1976d2', // Blue
      '#d32f2f', // Red
      '#388e3c', // Green
      '#f57c00', // Orange
      '#7b1fa2', // Purple
      '#0288d1', // Light Blue
      '#c2185b', // Pink
      '#fbc02d', // Yellow
      '#455a64', // Grey
      '#0097a7', // Cyan
    ];
    const colorMap = {};
    selectedMarkers.forEach((marker, index) => {
      colorMap[marker] = colors[index % colors.length];
    });
    return colorMap;
  }, [selectedMarkers]);

  return (
    <div className='text-markers-analysis' style={{ minHeight: '100vh', background: '#fff', display: 'flex' }}>
      <MarkersFilter
        selectedMarkers={selectedMarkers}
        setSelectedMarkers={setSelectedMarkers}
        sidebar={true}
      />
      <div className="main-content-area">
        <div className="action-section">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              className="show-data-btn"
              style={{ background: viewMode === 'chart' ? '#1976d2' : '#eee', color: viewMode === 'chart' ? '#fff' : '#1976d2', border: '1px solid #1976d2' }}
              onClick={() => setViewMode(viewMode === 'table' ? 'chart' : 'table')}
              disabled={selectedMarkers.length === 0 || !showTable || (viewMode === 'table' && selectedMarkers.filter(m => isNumericMarker(m)).length === 0)}
              title={viewMode === 'table' && selectedMarkers.filter(m => isNumericMarker(m)).length === 0 ? 'Выберите хотя бы один числовой маркер для отображения графика' : ''}
            >
              {viewMode === 'table' ? 'Показать график' : 'Показать таблицу'}
            </button>
            {viewMode === 'chart' && showTable && (
              <select value={aggregation} onChange={e => setAggregation(e.target.value)} style={{ fontSize: 16, padding: '6px 12px', borderRadius: 8, border: '1px solid #1976d2', color: '#1976d2', fontWeight: 500 }}>
                <option value="day">По дням</option>
                <option value="week">По неделям</option>
                <option value="month">По месяцам</option>
              </select>
            )}
          </div>
          {showTable && (
            <button 
              className="export-btn-markers" 
              onClick={() => setShowExportModal(true)}
              title="Экспортировать данные"
              disabled={loading || selectedMarkers.length === 0}
              style={{
                padding: "10px 14px",
                background: "#f4f6f8",
                color:  "#222",
                border: "1px solid #d1d5db",            
                borderRadius: 8,              
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 15,
                fontWeight: 500,
              }}
              onMouseEnter={(e) => {
                if (!loading && selectedMarkers.length > 0) {
                  e.currentTarget.style.background = "#e5e7eb";                
                  e.currentTarget.style.boxShadow = "0 4px 16px #3c3c3c3a";
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && selectedMarkers.length > 0) {
                  e.currentTarget.style.background = "#f4f6f8";
                  e.currentTarget.style.boxShadow = "0 0px 0px #3c3c3c3a";
                }
              }}
            >
              Экспорт данных
            </button>
          )}
        </div>
        
        {/* Модальное окно экспорта */}
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          dataType="text_markers"
          title="Экспорт психолингвистических показателей"
        />
        {showTable && viewMode === 'chart' && (
          <div style={{ minWidth: 1000, margin: '0 auto', background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px #0001', padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {chartError ? (
              <p className="error">{chartError}</p>
            ) : processedChartData.length === 0 && !chartLoading ? (
              <p className="no-data">Нет данных для отображения</p>
            ) : (
              <>
                {/* Индикатор загрузки данных */}
                {chartLoading && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 8, 
                    padding: '8px 16px', 
                    background: '#f0f9ff', 
                    borderRadius: 8, 
                    marginBottom: 12,
                    fontSize: 14,
                    color: '#1976d2'
                  }}>
                    <div style={{
                      border: '3px solid #f3f3f3',
                      borderTop: '3px solid #1976d2',
                      borderRadius: '50%',
                      width: '16px',
                      height: '16px',
                      animation: 'spin 1s linear infinite'
                    }} />
                    <span>
                      Загрузка данных... ({chunkedDataLoader.loadedChunksCount} из {chunkedDataLoader.totalChunksCount} блоков)
                    </span>
                  </div>
                )}
                
                {getActualDataLength(processedChartData) > 100 && (
                  <DataWindowSlider
                    aggregation={aggregation}
                    numOfVisible={100}
                    actualDataLength={getActualDataLength(processedChartData)}
                    onWindowChange={handleDataWindowChange}
                  />
                )}
                <div style={{ flex: '1 0 auto', position: 'relative', zIndex: 1 }}>
                <ChartsGroup
                  loading={chartLoading}
                  chartData={processedChartData}
                  dataWindow={dataWindow}
                  markerColors={markerColors}
                  getMarkerLabelFunction={getMarkerLabel}
                />
                </div>
              </>
            )}
          </div>
        )}
        {showTable && viewMode === 'table' && (
          <>
            <div className="table-scroll-container">
              {loading ? (
                <p className="loading">Загрузка...</p>
              ) : error ? (
                <p className="error">{error}</p>
              ) : data.length === 0 ? (
                <p className="no-data">Нет данных для отображения</p>
              ) : (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <th
                              key={header.id}
                              onClick={header.column.getToggleSortingHandler()}
                              className={header.column.getIsSorted() ? 'sorted' : ''}
                            >
                              {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                              {header.column.getIsSorted() === 'asc' ? ' 🔼' : header.column.getIsSorted() === 'desc' ? ' 🔽' : ''}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            {!loading && !error && data.length > 0 && (              
              <div className="pagination-sum">
              <div className="pagination-controls">
                <span>
                  Страница <strong>{table.getState().pagination.pageIndex + 1} из {table.getPageCount()}</strong>
                </span>
                <select
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => table.setPageSize(Number(e.target.value))}
                >
                  {[10, 20, 30, 50].map((size) => (
                    <option key={size} value={size}>
                      Показать {size}
                    </option>
                  ))}
                </select>
              </div>
            
              <Pagination
                currentPage={table.getState().pagination.pageIndex + 1}
                totalPages={table.getPageCount()}
                onPageChange={(newPage) => table.setPageIndex(newPage - 1)}
                maxVisiblePages={5}
              />
            </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TextMarkersAnalysis;